// frontend/src/components/Shared/AiContextModal.jsx
// Shown when the user enables AI — lets them pick their role/context
// so the AI responds with the right domain knowledge.
import React, { useState } from 'react';

// ── Preset roles ──────────────────────────────────────────────────────────────
const PRESETS = [
  {
    id: 'data-engineer',
    icon: '🗄️',
    label: 'Data Engineer',
    tags: 'SQL · Spark · ETL · Airflow · Kafka · Cloud Data',
    color: 'blue',
    systemPrompt: (extra) =>
      `You are an expert interview assistant helping a Data Engineer candidate in a live interview. ` +
      `The candidate has experience with SQL, Python, Apache Spark, ETL/ELT pipelines, Apache Airflow, Kafka, ` +
      `data warehousing (Snowflake, BigQuery, Redshift), and cloud platforms (AWS/GCP/Azure). ` +
      `When answering, focus on data engineering concepts, pipeline design, data quality, and cloud architecture.` +
      (extra ? ` Additional context about the candidate: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, and ready to say aloud.`
  },
  {
    id: 'qa-engineer',
    icon: '🧪',
    label: 'QA Engineer',
    tags: 'Selenium · API Testing · Test Strategy · JIRA · Agile',
    color: 'green',
    systemPrompt: (extra) =>
      `You are an expert interview assistant helping a QA Engineer candidate in a live interview. ` +
      `The candidate has experience with manual and automated testing, Selenium/Cypress, API testing (Postman/REST Assured), ` +
      `test management tools, defect tracking in JIRA, and agile/scrum methodologies. ` +
      `Focus on testing strategies, quality processes, bug lifecycle, and automation frameworks.` +
      (extra ? ` Additional context: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, and ready to say aloud.`
  },
  {
    id: 'software-engineer',
    icon: '👨‍💻',
    label: 'Software Engineer',
    tags: 'All SE domains · Coding · System Design · Data · DevOps · QA',
    color: 'purple',
    systemPrompt: (extra) =>
      `You are a senior software engineering interview assistant helping a candidate in a live interview. ` +
      `You are knowledgeable across ALL software engineering domains including: ` +
      `coding (Python, Java, JavaScript, SQL), data structures & algorithms, system design, ` +
      `databases (SQL/NoSQL), data engineering (ETL, Spark, Kafka, Airflow), ` +
      `cloud platforms (AWS, GCP, Azure), DevOps (Docker, Kubernetes, CI/CD, Terraform), ` +
      `frontend (React, TypeScript), backend (REST APIs, microservices, OOP), ` +
      `QA/testing (unit, integration, automation), and behavioral/situational questions. ` +
      `Answer any software engineering question clearly and accurately regardless of specialization.` +
      (extra ? ` Additional candidate context: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, confident, and ready to say aloud.`
  },
  {
    id: 'devops',
    icon: '☁️',
    label: 'DevOps / Cloud',
    tags: 'AWS · Kubernetes · Docker · CI/CD · Terraform · Linux',
    color: 'orange',
    systemPrompt: (extra) =>
      `You are an expert interview assistant helping a DevOps/Cloud Engineer candidate in a live interview. ` +
      `The candidate has experience with AWS/GCP/Azure, Docker, Kubernetes, CI/CD pipelines (Jenkins/GitHub Actions), ` +
      `Infrastructure as Code (Terraform/Ansible), and Linux administration.` +
      (extra ? ` Candidate context: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, and ready to say aloud.`
  },
  {
    id: 'data-scientist',
    icon: '📊',
    label: 'Data Scientist',
    tags: 'ML · Python · Statistics · TensorFlow · Pandas · NLP',
    color: 'pink',
    systemPrompt: (extra) =>
      `You are an expert interview assistant helping a Data Scientist candidate in a live interview. ` +
      `The candidate has experience with machine learning, Python (scikit-learn, TensorFlow, PyTorch), ` +
      `statistical analysis, feature engineering, model evaluation, NLP, and data visualization.` +
      (extra ? ` Candidate context: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, and ready to say aloud.`
  },
  {
    id: 'product-manager',
    icon: '📋',
    label: 'Product Manager',
    tags: 'Roadmap · Stakeholders · Agile · Metrics · Strategy',
    color: 'yellow',
    systemPrompt: (extra) =>
      `You are an expert interview assistant helping a Product Manager candidate in a live interview. ` +
      `Focus on product strategy, roadmap prioritization, stakeholder management, agile/scrum, ` +
      `data-driven decision making, user research, and go-to-market strategies.` +
      (extra ? ` Candidate context: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, and ready to say aloud.`
  },
  {
    id: 'frontend',
    icon: '🎨',
    label: 'Frontend Developer',
    tags: 'React · TypeScript · CSS · Performance · UX',
    color: 'teal',
    systemPrompt: (extra) =>
      `You are an expert interview assistant helping a Frontend Developer candidate in a live interview. ` +
      `Focus on React/Vue/Angular, TypeScript, CSS/Tailwind, web performance, accessibility, ` +
      `state management, REST/GraphQL integration, and UX best practices.` +
      (extra ? ` Candidate context: ${extra}` : '') +
      ` Keep answers concise (2-4 sentences), professional, and ready to say aloud.`
  },
  {
    id: 'custom',
    icon: '✍️',
    label: 'Custom Role',
    tags: 'Write your own context and prompt',
    color: 'gray',
    systemPrompt: (extra) => extra || ''
  }
];

const COLOR_MAP = {
  blue:   'border-blue-400 bg-blue-50 text-blue-700',
  green:  'border-green-400 bg-green-50 text-green-700',
  purple: 'border-purple-400 bg-purple-50 text-purple-700',
  orange: 'border-orange-400 bg-orange-50 text-orange-700',
  pink:   'border-pink-400 bg-pink-50 text-pink-700',
  yellow: 'border-yellow-400 bg-yellow-50 text-yellow-700',
  teal:   'border-teal-400 bg-teal-50 text-teal-700',
  gray:   'border-gray-400 bg-gray-50 text-gray-700',
};
const SELECTED_MAP = {
  blue:   'border-blue-500 bg-blue-600 text-white ring-2 ring-blue-300',
  green:  'border-green-500 bg-green-600 text-white ring-2 ring-green-300',
  purple: 'border-purple-500 bg-purple-600 text-white ring-2 ring-purple-300',
  orange: 'border-orange-500 bg-orange-600 text-white ring-2 ring-orange-300',
  pink:   'border-pink-500 bg-pink-600 text-white ring-2 ring-pink-300',
  yellow: 'border-yellow-500 bg-yellow-500 text-white ring-2 ring-yellow-300',
  teal:   'border-teal-500 bg-teal-600 text-white ring-2 ring-teal-300',
  gray:   'border-gray-500 bg-gray-600 text-white ring-2 ring-gray-300',
};

// ────────────────────────────────────────────────────────────────────────────
// Props:
//   onConfirm(systemPrompt, presetLabel)  — called when user clicks Enable AI
//   onCancel()                            — called when modal is dismissed
//   dark                                  — use dark theme (for StealthAgent)
// ────────────────────────────────────────────────────────────────────────────
const AiContextModal = ({ onConfirm, onCancel, dark = false }) => {
  // Default: Software Engineer — covers all SE problems broadly
  const [selectedId, setSelectedId]    = useState('software-engineer');
  const [extraContext, setExtraContext] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');

  const isCustom   = selectedId === 'custom';
  const preset     = PRESETS.find(p => p.id === selectedId);
  const canConfirm = selectedId && (!isCustom || customPrompt.trim());

  const handleConfirm = () => {
    if (!canConfirm) return;
    let systemPrompt;
    if (isCustom) {
      systemPrompt = customPrompt.trim();
    } else {
      systemPrompt = preset.systemPrompt(extraContext.trim());
    }
    onConfirm(systemPrompt, preset?.label || 'Custom');
  };

  const bg      = dark ? 'bg-gray-950'  : 'bg-white';
const textPri = dark ? 'text-white'   : 'text-gray-900';
  const textSec = dark ? 'text-gray-400' : 'text-gray-500';
  const inputCls = dark
    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:ring-blue-500'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-purple-500';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`w-full max-w-2xl rounded-2xl shadow-2xl ${bg} p-6 my-4`}>

        {/* Header */}
        <div className="flex items-center space-x-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-blue-600 flex items-center justify-center flex-shrink-0">
            <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
            </svg>
          </div>
          <div>
            <h2 className={`text-lg font-bold ${textPri}`}>Set AI Context</h2>
            <p className={`text-sm ${textSec}`}>
              Tell the AI what role you're interviewing for so it gives relevant answers
            </p>
          </div>
          <button onClick={onCancel} className={`ml-auto ${textSec} hover:text-gray-400 p-1`}>
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Role grid */}
        <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${textSec}`}>
          Select your role / interview type
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {PRESETS.map(p => {
            const isSelected = selectedId === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`rounded-xl border-2 p-3 text-left transition-all
                  ${isSelected ? SELECTED_MAP[p.color] : (dark ? 'border-gray-700 bg-gray-900 text-gray-300 hover:border-gray-500' : COLOR_MAP[p.color] + ' opacity-70 hover:opacity-100')}`}
              >
                <span className="text-2xl block mb-1">{p.icon}</span>
                <span className="font-semibold text-xs block leading-tight">{p.label}</span>
                <span className={`text-xs block mt-0.5 leading-tight ${isSelected ? 'opacity-70' : (dark ? 'text-gray-500' : 'opacity-60')}`}>
                  {p.tags}
                </span>
              </button>
            );
          })}
        </div>

        {/* Extra context field — for non-custom presets */}
        {selectedId && !isCustom && (
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-1.5 ${textPri}`}>
              Add your specific experience / skills
              <span className={`ml-1 font-normal ${textSec}`}>(optional but recommended)</span>
            </label>
            <textarea
              rows={3}
              value={extraContext}
              onChange={e => setExtraContext(e.target.value)}
              placeholder={`e.g. "5 years at fintech startup, built real-time Kafka pipelines on AWS, led migration from Oracle to Snowflake"`}
              className={`w-full px-3 py-2.5 border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 ${inputCls}`}
            />
            <p className={`text-xs mt-1 ${textSec}`}>
              This helps the AI tailor answers to your actual background.
            </p>
          </div>
        )}

        {/* Full custom prompt — only for 'custom' preset */}
        {isCustom && (
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-1.5 ${textPri}`}>
              Write your custom AI instructions <span className="text-red-400">*</span>
            </label>
            <textarea
              rows={5}
              value={customPrompt}
              onChange={e => setCustomPrompt(e.target.value)}
              placeholder={
                `e.g. "You are helping a Senior Network Engineer candidate. Focus on BGP routing, MPLS, SD-WAN, Cisco IOS, and network security. Keep answers under 3 sentences, professional, and ready to say aloud."`
              }
              className={`w-full px-3 py-2.5 border rounded-xl text-sm resize-none focus:outline-none focus:ring-2 ${inputCls}`}
            />
            <p className={`text-xs mt-1 ${textSec}`}>
              Describe the role, tech stack, and how the AI should respond.
            </p>
          </div>
        )}

        {/* Preview of generated prompt */}
        {selectedId && !isCustom && preset && (
          <details className={`mb-4 rounded-xl border ${dark ? 'border-gray-700' : 'border-gray-200'} overflow-hidden`}>
            <summary className={`px-4 py-2.5 cursor-pointer text-xs font-medium select-none ${dark ? 'bg-gray-900 text-gray-400' : 'bg-gray-50 text-gray-600'}`}>
              Preview AI system prompt
            </summary>
            <div className={`px-4 py-3 text-xs font-mono leading-relaxed ${dark ? 'bg-gray-950 text-gray-400' : 'bg-white text-gray-600'}`}>
              {preset.systemPrompt(extraContext.trim() || '(your context here)')}
            </div>
          </details>
        )}

        {/* Actions */}
        <div className="flex space-x-3">
          <button onClick={onCancel}
            className={`px-5 py-2.5 rounded-xl text-sm font-medium border transition-colors
              ${dark ? 'border-gray-700 text-gray-400 hover:bg-gray-900' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold rounded-xl text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-lg"
          >
            {canConfirm
              ? `✓ Enable AI for ${isCustom ? 'Custom Role' : preset?.label}`
              : 'Select a role to continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiContextModal;
export { PRESETS };
